/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { queryCache } from 'react-query';
import { all, call, put, takeLatest } from 'redux-saga/effects';
import { v4 as uuid } from 'uuid';

import { AuthenticationApiService } from '../../services/AuthenticationApiService';
import { UserApiService } from '../../services/UserApiService';
import { ApiClientError } from '../../types/api/ApiClientError';
import { ApiClientResponse } from '../../types/api/ApiClientResponse';
import { IUser } from '../../types/user/IUser';
import { debounceJwtRefresh } from '../../utils/debounceJwtRefresh';
import { fetchSystemStatus } from '../../utils/queryFns';
import {
  authLogoutActions,
  authRefreshUserActions,
  authSetUser,
  checkUserAuth,
  checkUserAuthActions,
  userLogin,
} from './actions';
import { AuthActions, AuthLogoutWatcherAction, AuthorizeUserByTokenAction } from './types';

export function* logoutUser(skipLogoutRequest: boolean = false) {
  try {
    if (!skipLogoutRequest) {
      yield call(AuthenticationApiService.logout);
    }
  } catch (e) {
    console.error(e);
  }

  yield call([localStorage, 'removeItem'], 'current_org');
  yield call([localStorage, 'removeItem'], 'expire');
  yield call([localStorage, 'removeItem'], 'uuid');
  yield call([localStorage, 'removeItem'], 'jwt_token');
  yield call([localStorage, 'removeItem'], 'jwt_expires');

  void queryCache.invalidateQueries([fetchSystemStatus.key]);
}

export function* checkUserSaga() {
  try {
    const refreshResult: Awaited<ReturnType<typeof AuthenticationApiService.refresh>> = yield call([
      AuthenticationApiService,
      'refresh',
    ]);

    const { accessToken, expires } = refreshResult.data.payload;
    yield call([localStorage, 'setItem'], 'jwt_token', accessToken);
    yield call([localStorage, 'setItem'], 'jwt_expires', expires);
    debounceJwtRefresh(new Date(expires));

    const result: ApiClientResponse<IUser> = yield call(UserApiService.getUser);
    yield put(checkUserAuthActions.authorized(result.data.payload));
    yield put(userLogin(result.data.payload));
  } catch (e) {
    const error = e as ApiClientError;
    console.error(error);
    if (error.response?.status === 401 && error.response?.data.message === 'unauthorized') {
      yield call(logoutUser, true);
    }
    yield put(checkUserAuthActions.notAuthorized(error.response?.data.message));
  }
}

export function* logoutUserSaga(action: AuthLogoutWatcherAction) {
  const { isBySystem } = action.payload;
  try {
    yield call(logoutUser);
    yield put(authLogoutActions.logout(isBySystem));
  } catch (e) {
    const error = e as ApiClientError;
    console.error(error);
  }
}

export function* refreshUserSaga() {
  try {
    // Refresh the JWT
    const refreshResult: Awaited<ReturnType<typeof AuthenticationApiService.refresh>> = yield call([
      AuthenticationApiService,
      'refresh',
    ]);

    const { accessToken, expires } = refreshResult.data.payload;
    yield call([localStorage, 'setItem'], 'jwt_token', accessToken);
    yield call([localStorage, 'setItem'], 'jwt_expires', expires);
    debounceJwtRefresh(new Date(expires));

    // Refresh the user
    yield put(authRefreshUserActions.start());
    const result: ApiClientResponse<IUser> = yield call(UserApiService.getUser);
    yield put(authSetUser(result.data.payload));
  } catch (e) {
    const error = e as ApiClientError;
    console.error(error);
  } finally {
    yield put(authRefreshUserActions.completed());
  }
}

export function* authorizeUserByTokenSaga(action: AuthorizeUserByTokenAction) {
  try {
    const { token, expires } = action.payload;
    yield call([localStorage, 'setItem'], 'jwt_token', token);
    yield call([localStorage, 'setItem'], 'jwt_expires', expires.toISOString());
    debounceJwtRefresh(expires);
    yield call([localStorage, 'setItem'], 'uuid', uuid());
    const result: ApiClientResponse<IUser> = yield call(UserApiService.getUser);
    yield put(authSetUser(result.data.payload));
    yield put(userLogin(result.data.payload));
  } catch (e) {
    const error = e as ApiClientError;
    console.error(error);
    yield put(authLogoutActions.logout());
  }
}

export function* authMainSaga(): Generator {
  yield all([
    yield takeLatest([AuthActions.AUTH_LOGOUT_WATCHER], logoutUserSaga),
    yield takeLatest([AuthActions.AUTH_CHECK_START], checkUserSaga),
    yield takeLatest([AuthActions.AUTH_REFRESH_USER_WATCHER], refreshUserSaga),
    yield takeLatest([AuthActions.AUTHORIZE_USER_BY_TOKEN_WATCHER], authorizeUserByTokenSaga),
  ]);
  yield put(checkUserAuth());
}
